import { Component, Inject, OnInit } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { AuthConfig, AuthService } from 'src/app/Services/auth.service';
import { DashboardService } from 'src/app/Services/dashboard.service';
import { ArtistDashboard } from 'src/app/models/response/artistdashboard.models';
import { OnBoardingService } from 'src/app/Services/shared/onboarding.service';
import { ActivityFeedService, ActivityItem } from 'src/app/Services/shared/activity-feed.service';
import { ImageUtils } from 'src/app/shared/utils/image-utils';
import { ArtistWebsiteService } from 'src/app/Services/artist-website.service';

@Component({
    selector: 'ds-artist-dashboard',
    templateUrl: './ds-artist-dashboard.component.html',
    styleUrls: ['./ds-artist-dashboard.component.css'],
    providers: [DashboardService],
    standalone: false
})
export class DsArtistDashboardComponent implements OnInit {
    public dashboardData: ArtistDashboard = {
        IncomingArtistConnectionRequest: []
    };
    public artistId!: string;
    public firstName: string = '';
    public dashboardDataLoaded: boolean = false;
    public selectedTabIndex: number = 0;
    public isAuthenticated!: boolean;
    public isArtist: boolean = false;
    public showDashboard: boolean = true;
    public authConfig: AuthConfig = {};

    /**
     * What other people are waiting on — enquiries, gallery product requests, invitations.
     *
     * Recent Activity beside it shows what the artist has done; this shows what has been done to
     * them. Sourced through ActivityFeedService, which is the single place that knows these are
     * currently three separate calls (DW-016).
     */
    public activity: ActivityItem[] = [];
    public activityLoading = true;

    /**
     * The artist's published website, as this card advertises it.
     *
     * Every value here used to be invented: the status read "Published" unconditionally, the URL
     * text was the artist's first name, the href was `portal.artflow-pro.com/profile/{GUID}` —
     * a hardcoded host that is not the one the artist is signed in to, pointing at an id rather
     * than the published slug — and the two stats were the consignment count and the recent-artwork
     * count wearing other labels. An artist cannot share a site whose address they are never shown.
     */
    public siteLoading = true;
    public sitePublished = false;
    public siteSlug: string | null = null;
    public sitePublishedDate: string | null = null;
    public siteCopied = false;

    /** Absolute, shareable, and on the host the artist is actually using. */
    public get siteUrl(): string {
        if (!this.siteSlug) { return ''; }
        return this.document.location.origin + '/profile/' + this.siteSlug;
    }

    /** The same address without the scheme — what reads well on a card. */
    public get siteUrlDisplay(): string {
        return this.siteUrl.replace(/^https?:\/\//, '');
    }

    public get activityUnread(): number {
        return this.activity.filter((a) => a.unread).length;
    }

    constructor(
        private ngxSpinner: NgxSpinnerService,
        private router: Router,
        private authService: AuthService,
        private dashboardService: DashboardService,
        private onBoardingService: OnBoardingService,
        private activityFeedService: ActivityFeedService,
        private artistWebsiteService: ArtistWebsiteService,
        @Inject(DOCUMENT) private document: Document
    ) { }

    /**
     * Loads the attention feed. Deliberately not gated on a spinner and never fatal: the dashboard
     * is useful without it, so a slow or failed feed leaves an empty panel rather than holding up
     * or breaking the page.
     */
    private loadActivity(): void {
        if (!this.artistId) { this.activityLoading = false; return; }
        this.activityFeedService.getFeed(this.artistId).subscribe({
            next: (items) => { this.activity = items; this.activityLoading = false; },
            error: () => { this.activity = []; this.activityLoading = false; },
        });
    }

    /**
     * Reads the artist's own site record so the card can state the truth: whether anything is
     * published, and if so the address to share.
     *
     * Non-fatal, like the activity feed — `Success:false` here just means "no site yet", which is
     * the common case for a new artist and not an error worth surfacing.
     */
    private loadSiteStatus(): void {
        if (!this.artistId) { this.siteLoading = false; return; }
        this.artistWebsiteService.getDraft(this.artistId).subscribe({
            next: (res) => {
                const site = res && res.Success ? res.ResponseData : null;
                this.sitePublished = !!(site && site.IsPublished && site.Slug);
                this.siteSlug = site ? site.Slug : null;
                this.sitePublishedDate = site ? site.PublishedDate : null;
                this.siteLoading = false;
            },
            error: () => { this.siteLoading = false; },
        });
    }

    /** Puts the address on the clipboard — the action an artist actually wants from this card. */
    public copySiteUrl(): void {
        if (!this.siteUrl) { return; }
        navigator.clipboard.writeText(this.siteUrl).then(() => {
            this.siteCopied = true;
            setTimeout(() => { this.siteCopied = false; }, 2000);
        }).catch(() => { /* clipboard blocked — the address is on screen to copy by hand */ });
    }

    /** Opens the builder, which is where an unpublished site gets published. */
    public openWebsiteBuilder(): void {
        this.router.navigate(['/website-builder']);
    }

    public ngOnInit(): void {
        this.getArtistDashboard();
        this.artistId = this.authService.getUserId();
        this.loadActivity();
        this.loadSiteStatus();
        const currentUser = this.authService.getCurrentUser();
        this.firstName = currentUser?.FirstName || 'Artist';
        this.isAuthenticated = this.authService.isAuthenticated();
        if (this.isAuthenticated) {
            this.authConfig = this.authService.getAuthConfig();
            this.isArtist = this.authConfig.IsArtist || false;
            this.onBoardingService.getUpdatedLocalStorageValue.subscribe((tourVisible: boolean) => {
                this.showDashboard = !tourVisible;
            });
        }
    }

    public getArtistDashboard() {
        this.ngxSpinner.show();
        this.dashboardService.getArtistDashboard().subscribe((data: ArtistDashboard) => {
            data.BestPerformingSeriesData?.forEach((item) => {
                const colorClass = this.getColorBasedOnPercentage(item.PercentageOfTotal);
                item.ColorClassBasedOnPercentage = 'bg-' + colorClass;
                item.TextBasedOnPercentage = `<span class="text-${colorClass}">${item.PercentageOfTotal}%</span>`;
            });
            this.dashboardData = data;
            if (data.RecentSales) {
                this.dashboardData.RecentSales = data.RecentSales.map((item) => {
                    item.ResizedImageUrl = ImageUtils.getResizedCloudinaryUrl(item.ProductImageUrl || 'assets/images/No-Image.png');
                    return item;
                });
            }
            if (data.NewProductsAdded) {
                this.dashboardData.NewProductsAdded = data.NewProductsAdded.map((item) => {
                    item.ResizedImageUrl = ImageUtils.getResizedCloudinaryUrl(item.ProductImageUrl || 'assets/images/No-Image.png');
                    return item;
                });
            }
            this.ngxSpinner.hide();
            this.dashboardDataLoaded = true;
        }, error => {
            console.error(error);
            this.ngxSpinner.hide();
        });
    }

    private getColorBasedOnPercentage(percentage: number): string {
        if (percentage >= 75) {
            return 'success';
        } else if (percentage >= 50) {
            return 'info';
        } else if (percentage >= 25) {
            return 'warning';
        }
        return 'danger';
    }

    public goToProductDetails(product: any) {
        this.router.navigate(['/artists', 'artworks', product.ProductId]);
    }

    public goToProductRequestPage() {
        this.router.navigate(['/artists', 'product-requests']);
    }

    public onViewConsignmentsClicked(data: any) {
        if (data && data.GalleryId) {
            this.router.navigate(['/artists', 'gallery', data.GalleryId, 'gallery-products']);
        } else {
            console.error('Invalid data object:', data);
        }
    }

    public ChatwithGallery(data: any) {
        if (data && data.Id) {
            this.router.navigate(['/inbox', data.Id]);
        } else {
            console.error('Invalid data object:', data);
        }
    }

    getFirstSentence(message: string, wordCount: number = 10): string {
        if (!message) return '';
        const words = message.split(' ');
        if (words.length <= wordCount) {
            return message;
        }
        return words.slice(0, wordCount).join(' ') + '...';
    }

    public onTabChange(event: any) {
        if (event.index !== undefined) {
            this.selectedTabIndex = event.index;
        }
    }

    public navigateToArtworks() {
        this.router.navigate(['/artists', 'artworks']);
    }

    public navigateToCreateArtwork() {
        this.router.navigate(['/artists', 'artworks', 'create']);
    }

    public navigateToConnectedGalleries() {
        this.router.navigate(['/artists', 'connected-galleries']);
    }

    public navigateToConsignments() {
        this.router.navigate(['/view-consignments']);
    }

    public navigateToEvents() {
        this.router.navigate(['/events', 'my-participated-events']);
    }

    public navigateToPortfolio() {
        this.router.navigate(['/artists', 'portfolio']);
    }

    public navigateToMarketing() {
        this.router.navigate(['/social-media']);
    }

    public sharePortfolio() {
        const portfolioUrl = this.siteUrl;
        if (!portfolioUrl) { this.openWebsiteBuilder(); return; }
        if (navigator.share) {
            navigator.share({
                title: 'My Art Portfolio',
                text: 'Check out my art portfolio!',
                url: portfolioUrl
            });
        } else {
            navigator.clipboard.writeText(portfolioUrl);
        }
    }

    public viewPortfolio() {
        if (!this.siteUrl) { this.openWebsiteBuilder(); return; }
        window.open(this.siteUrl, '_blank');
    }
}
